import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Dropdown, Badge, Button, Form, Spinner, Modal } from 'react-bootstrap';
import { useLanguage } from '../../context/LanguageContext';
import { translations } from '../../i18n/translations';
import api from '../../services/api';
import { API_TOKEN_ALERT_DISMISS_KEY } from '../../utils/notificationsSession';

function loadDismissedMap() {
  try {
    const raw = sessionStorage.getItem(API_TOKEN_ALERT_DISMISS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveDismissedMap(map) {
  try {
    sessionStorage.setItem(API_TOKEN_ALERT_DISMISS_KEY, JSON.stringify(map));
  } catch (_) {
    /* ignore */
  }
}

function pickI18n(obj, lang) {
  if (!obj || typeof obj !== 'object') return '';
  return obj[lang] || obj.en || Object.values(obj).find((x) => typeof x === 'string' && x.trim()) || '';
}

function notifString(lang, key, params = {}) {
  const pack = translations[lang]?.notifications || translations.en?.notifications;
  let s = pack?.[key];
  if (typeof s !== 'string') s = translations.en?.notifications?.[key] || '';
  Object.entries(params).forEach(([k, v]) => {
    s = s.replace(new RegExp(`{{${k}}}`, 'g'), String(v ?? ''));
  });
  return s;
}

function extractImageUrlFromLine(line) {
  const markdownMatch = line.match(/!\[[^\]]*]\((https?:\/\/[^)\s]+)\)/i);
  if (markdownMatch?.[1]) return markdownMatch[1];

  const htmlMatch = line.match(/<img[^>]*src=["']([^"']+)["'][^>]*>/i);
  if (htmlMatch?.[1]) return htmlMatch[1];

  const xmlMatch = line.match(/<image>\s*([^<\s]+)\s*<\/image>/i);
  if (xmlMatch?.[1]) return xmlMatch[1];

  const bracketMatch = line.match(/\[image:\s*([^\]]+)\]/i);
  if (bracketMatch?.[1]) return bracketMatch[1].trim();

  return null;
}

function isRenderableImageUrl(url) {
  if (!url || typeof url !== 'string') return false;
  return (
    /^https?:\/\//i.test(url) ||
    url.startsWith('/') ||
    url.startsWith('./') ||
    url.startsWith('../')
  );
}

function parseBulletinBody(bodyText) {
  if (!bodyText || typeof bodyText !== 'string') return [];

  const lines = bodyText
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  return lines.map((line) => {
    const imageUrl = extractImageUrlFromLine(line);
    if (imageUrl && isRenderableImageUrl(imageUrl)) {
      return { type: 'image', value: imageUrl };
    }
    return { type: 'text', value: line };
  });
}

const BULLETIN_LANG_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'ru', label: 'Русский' },
  { code: 'zh', label: '中文' },
  { code: 'tr', label: 'Türkçe' },
  { code: 'ar', label: 'العربية' },
];

const BULLETIN_POPUP_POSITION_KEY = 'system_bulletin_popup_position_v2';
const BULLETIN_POPUP_HIDDEN_IDS_KEY = 'system_bulletin_popup_hidden_ids_v1';

const DEFAULT_POPUP_WIDTH = 432; // Notifications-like width (360) +20%
const DEFAULT_POPUP_HEIGHT = 720; // Notifications-like height (480) +50%

function loadPopupPosition() {
  try {
    const raw = localStorage.getItem(BULLETIN_POPUP_POSITION_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const left = Number(parsed?.left);
    const top = Number(parsed?.top);
    if (Number.isFinite(left) && Number.isFinite(top)) {
      return { left, top };
    }
  } catch (_) {
    /* ignore */
  }
  return null;
}

function savePopupPosition(position) {
  try {
    localStorage.setItem(BULLETIN_POPUP_POSITION_KEY, JSON.stringify(position));
  } catch (_) {
    /* ignore */
  }
}

function loadHiddenPopupBulletinIds() {
  try {
    const raw = localStorage.getItem(BULLETIN_POPUP_HIDDEN_IDS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (Array.isArray(parsed)) return parsed.map((x) => Number(x)).filter((x) => Number.isFinite(x));
  } catch (_) {
    /* ignore */
  }
  return [];
}

function saveHiddenPopupBulletinIds(ids) {
  try {
    localStorage.setItem(BULLETIN_POPUP_HIDDEN_IDS_KEY, JSON.stringify(ids));
  } catch (_) {
    /* ignore */
  }
}

function clampPopupPosition(position) {
  if (typeof window === 'undefined') return position;
  const margin = 8;
  const maxLeft = Math.max(margin, window.innerWidth - DEFAULT_POPUP_WIDTH - margin);
  const maxTop = Math.max(margin, window.innerHeight - DEFAULT_POPUP_HEIGHT - margin);
  return {
    left: Math.min(maxLeft, Math.max(margin, position.left)),
    top: Math.min(maxTop, Math.max(margin, position.top)),
  };
}

function defaultPopupPosition() {
  if (typeof window === 'undefined') return { left: 16, top: 16 };
  return clampPopupPosition(
    {
      left: window.innerWidth - DEFAULT_POPUP_WIDTH - 16,
      top: window.innerHeight - DEFAULT_POPUP_HEIGHT - 16,
    },
  );
}

/**
 * @param {object[]} stores — from Dashboard loadStores
 * @param {(store: object) => void} onOpenApiKeyModal
 */
export default function SystemNotificationsMenu({ stores = [], onOpenApiKeyModal }) {
  const { t, language } = useLanguage();
  const closeGuardTimerRef = useRef(null);
  const [bulletins, setBulletins] = useState([]);
  const [loadingBulletins, setLoadingBulletins] = useState(false);
  const [langByBulletinId, setLangByBulletinId] = useState({});
  const [langByTokenStoreId, setLangByTokenStoreId] = useState({});
  const [dismissedApiToken, setDismissedApiToken] = useState(() => loadDismissedMap());
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [suppressRootClose, setSuppressRootClose] = useState(false);
  const [popupVisible, setPopupVisible] = useState(false);
  const [popupBulletinId, setPopupBulletinId] = useState(null);
  const [popupPosition, setPopupPosition] = useState(() => loadPopupPosition());
  const [hiddenPopupBulletinIds, setHiddenPopupBulletinIds] = useState(() => loadHiddenPopupBulletinIds());
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const dragRef = useRef(null);

  const failedStores = useMemo(
    () => (stores || []).filter((s) => s.verification_status === 'failed'),
    [stores],
  );

  const visibleTokenAlerts = useMemo(
    () => failedStores.filter((s) => !dismissedApiToken[String(s.id)]),
    [failedStores, dismissedApiToken],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingBulletins(true);
      try {
        const res = await api.getSystemBulletins();
        if (!cancelled && Array.isArray(res?.data)) setBulletins(res.data);
      } catch {
        if (!cancelled) setBulletins([]);
      } finally {
        if (!cancelled) setLoadingBulletins(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const unreadBulletins = useMemo(() => bulletins.filter((b) => !b.read), [bulletins]);
  const popupUnreadBulletins = useMemo(
    () => unreadBulletins.filter((b) => !hiddenPopupBulletinIds.includes(Number(b.id))),
    [unreadBulletins, hiddenPopupBulletinIds],
  );
  const badgeCount = unreadBulletins.length + visibleTokenAlerts.length;

  const bulletinDisplayLang = useCallback(
    (id) => langByBulletinId[id] || language,
    [langByBulletinId, language],
  );

  const markBulletinRead = async (id) => {
    setBulletins((prev) => prev.map((b) => (b.id === id ? { ...b, read: true } : b)));
    try {
      await api.markSystemBulletinRead(id);
    } catch (_) {
      /* ignore */
    }
  };

  const dismissTokenAlert = (storeId) => {
    const next = { ...dismissedApiToken, [String(storeId)]: true };
    setDismissedApiToken(next);
    saveDismissedMap(next);
  };

  useEffect(() => {
    if (popupVisible) return;
    const firstUnread = popupUnreadBulletins[0];
    if (!firstUnread) return;
    setPopupBulletinId(firstUnread.id);
    setPopupVisible(true);
  }, [popupUnreadBulletins, popupVisible]);

  useEffect(() => {
    if (!popupPosition) {
      setPopupPosition(defaultPopupPosition());
      return;
    }
    savePopupPosition(popupPosition);
  }, [popupPosition]);

  useEffect(() => {
    saveHiddenPopupBulletinIds(hiddenPopupBulletinIds);
  }, [hiddenPopupBulletinIds]);

  useEffect(() => () => {
    if (closeGuardTimerRef.current) {
      clearTimeout(closeGuardTimerRef.current);
    }
  }, []);

  useEffect(() => {
    if (!popupPosition) return;
    const onWindowResize = () => {
      setPopupPosition((prev) => (prev ? clampPopupPosition(prev) : prev));
    };
    window.addEventListener('resize', onWindowResize);
    return () => window.removeEventListener('resize', onWindowResize);
  }, [popupPosition]);

  const renderBulletinBody = useCallback((bulletin, dl) => (
    <div className="small text-muted mt-1 d-flex flex-column gap-2">
      {parseBulletinBody(pickI18n(bulletin.body, dl)).map((block, index) =>
        block.type === 'image' ? (
          <img
            key={`${bulletin.id}-img-${index}`}
            src={block.value}
            alt=""
            loading="lazy"
            onClick={() => setPreviewImageUrl(block.value)}
            style={{
              width: '100%',
              maxHeight: 220,
              objectFit: 'contain',
              borderRadius: 8,
              background: '#fff',
              cursor: 'zoom-in',
            }}
          />
        ) : (
          <div key={`${bulletin.id}-txt-${index}`} style={{ whiteSpace: 'pre-wrap' }}>
            {block.value}
          </div>
        ),
      )}
    </div>
  ), []);

  const popupBulletin = useMemo(() => {
    const exact = popupUnreadBulletins.find((b) => b.id === popupBulletinId);
    return exact || popupUnreadBulletins[0] || null;
  }, [popupUnreadBulletins, popupBulletinId]);

  const handlePopupAcknowledge = useCallback(async () => {
    if (!popupBulletin) {
      setPopupVisible(false);
      return;
    }
    await markBulletinRead(popupBulletin.id);
    setPopupVisible(false);
    setPopupBulletinId(null);
  }, [popupBulletin]);

  const handlePopupCloseOnly = useCallback(() => {
    if (popupBulletin?.id) {
      setHiddenPopupBulletinIds((prev) => {
        const next = Array.from(new Set([...prev, Number(popupBulletin.id)]));
        return next;
      });
    }
    setPopupVisible(false);
    setPopupBulletinId(null);
  }, [popupBulletin]);

  const guardDropdownFromRootClose = useCallback(() => {
    setSuppressRootClose(true);
    if (closeGuardTimerRef.current) {
      clearTimeout(closeGuardTimerRef.current);
    }
    closeGuardTimerRef.current = setTimeout(() => {
      setSuppressRootClose(false);
      closeGuardTimerRef.current = null;
    }, 220);
  }, []);

  const handleCloseImagePreview = useCallback(() => {
    guardDropdownFromRootClose();
    setPreviewImageUrl(null);
  }, [guardDropdownFromRootClose]);

  const handleDropdownToggle = useCallback(
    (nextShow, meta = {}) => {
      const source = meta?.source;
      if (source === 'rootClose' && (Boolean(previewImageUrl) || suppressRootClose)) {
        return;
      }
      setDropdownOpen(nextShow);
    },
    [previewImageUrl, suppressRootClose],
  );

  const startDragging = useCallback(
    (event) => {
      if (!popupPosition) return;
      event.preventDefault();
      if (event.button !== 0) return;
      const startX = event.clientX;
      const startY = event.clientY;
      const startLeft = popupPosition.left;
      const startTop = popupPosition.top;
      dragRef.current = true;

      const onMove = (moveEvent) => {
        if (!dragRef.current) return;
        const next = clampPopupPosition(
          {
            left: startLeft + (moveEvent.clientX - startX),
            top: startTop + (moveEvent.clientY - startY),
          },
        );
        setPopupPosition(next);
      };

      const onUp = () => {
        dragRef.current = null;
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [popupPosition],
  );

  return (
    <>
      <Dropdown
        align="end"
        autoClose="outside"
        show={dropdownOpen}
        onToggle={handleDropdownToggle}
      >
        <Dropdown.Toggle variant="outline-secondary" className="nav-pill-btn position-relative">
          <i className="bi bi-bell" />
          <span className="d-none d-sm-inline ms-1">{t('nav.notifications')}</span>
          {badgeCount > 0 && (
            <Badge
              bg="danger"
              pill
              className="position-absolute"
              style={{ top: '-5px', right: '-5px', fontSize: '0.65rem' }}
            >
              {badgeCount > 99 ? '99+' : badgeCount}
            </Badge>
          )}
        </Dropdown.Toggle>
        <Dropdown.Menu style={{ width: 360, maxHeight: 480, overflowY: 'auto' }} className="p-2 shadow">
          {loadingBulletins && (
            <div className="text-center py-2">
              <Spinner animation="border" size="sm" />
            </div>
          )}
          {!loadingBulletins && !bulletins.length && !visibleTokenAlerts.length && (
            <div className="text-muted small px-2 py-2">{t('notifications.empty')}</div>
          )}
          {visibleTokenAlerts.map((store) => {
            const tl = langByTokenStoreId[store.id] || language;
            return (
            <div key={`token-${store.id}`} className="border rounded p-2 mb-2 bg-light">
              <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                <strong className="small">{notifString(tl, 'apiTokenTitle')}</strong>
              </div>
              <Form.Select
                size="sm"
                className="mb-2"
                value={tl}
                onChange={(e) =>
                  setLangByTokenStoreId((prev) => ({ ...prev, [store.id]: e.target.value }))
                }
                aria-label={t('notifications.languageLabel')}
              >
                {BULLETIN_LANG_OPTIONS.map((o) => (
                  <option key={o.code} value={o.code}>
                    {o.label}
                  </option>
                ))}
              </Form.Select>
              <p className="small mb-2">
                {notifString(tl, 'apiTokenBody', {
                  storeName: store.name || store.store_id || String(store.id),
                })}
              </p>
              <div className="d-flex flex-wrap gap-2">
                <Button size="sm" variant="warning" type="button" onClick={() => onOpenApiKeyModal && onOpenApiKeyModal(store)}>
                  {notifString(tl, 'addApiToken')}
                </Button>
                <Button size="sm" variant="outline-secondary" type="button" onClick={() => dismissTokenAlert(store.id)}>
                  {notifString(tl, 'apiTokenDismiss')}
                </Button>
              </div>
            </div>
            );
          })}
          {bulletins.map((b) => {
            const dl = bulletinDisplayLang(b.id);
            return (
              <div
                key={b.id}
                className={`border rounded p-2 mb-2 ${b.read ? 'opacity-75' : 'border-warning'}`}
              >
                <div className="d-flex justify-content-between align-items-center gap-2 mb-1">
                  <Form.Select
                    size="sm"
                    style={{ maxWidth: 140 }}
                    value={dl}
                    onChange={(e) =>
                      setLangByBulletinId((prev) => ({ ...prev, [b.id]: e.target.value }))
                    }
                    aria-label={t('notifications.languageLabel')}
                  >
                    {BULLETIN_LANG_OPTIONS.map((o) => (
                      <option key={o.code} value={o.code}>
                        {o.label}
                      </option>
                    ))}
                  </Form.Select>
                  {!b.read && (
                    <Button variant="link" size="sm" className="text-nowrap p-0" type="button" onClick={() => markBulletinRead(b.id)}>
                      {t('notifications.markRead')}
                    </Button>
                  )}
                </div>
                <div className="fw-semibold small">{pickI18n(b.title, dl)}</div>
                {renderBulletinBody(b, dl)}
                <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                  {b.created_at}
                </div>
              </div>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>

      {popupVisible && popupBulletin && popupPosition && (
        <div
          className="shadow border rounded"
          style={{
            position: 'fixed',
            left: popupPosition.left,
            top: popupPosition.top,
            zIndex: 1060,
            width: DEFAULT_POPUP_WIDTH,
            height: DEFAULT_POPUP_HEIGHT,
            maxWidth: '90vw',
            maxHeight: '85vh',
            background: 'var(--card-bg, #fff)',
            overflow: 'hidden',
          }}
        >
          <div className="p-2 d-flex flex-column h-100" style={{ overflowY: 'auto', paddingBottom: 14 }}>
            <div
              className="d-flex justify-content-between align-items-center gap-2 mb-2"
              style={{ cursor: 'move', userSelect: 'none' }}
              onMouseDown={startDragging}
            >
              <div className="fw-semibold small">{t('nav.notifications')}</div>
              <Button variant="link" size="sm" className="p-0 text-muted" onClick={handlePopupCloseOnly}>
                <i className="bi bi-x-lg" />
              </Button>
            </div>
            <div className="border rounded p-2 mb-2">
              <div className="d-flex justify-content-between align-items-center gap-2 mb-1">
                <Form.Select
                  size="sm"
                  style={{ maxWidth: 140 }}
                  value={bulletinDisplayLang(popupBulletin.id)}
                  onChange={(e) =>
                    setLangByBulletinId((prev) => ({ ...prev, [popupBulletin.id]: e.target.value }))
                  }
                  aria-label={t('notifications.languageLabel')}
                >
                  {BULLETIN_LANG_OPTIONS.map((o) => (
                    <option key={o.code} value={o.code}>
                      {o.label}
                    </option>
                  ))}
                </Form.Select>
                <Button size="sm" variant="warning" type="button" onClick={handlePopupAcknowledge}>
                  {t('notifications.markRead')}
                </Button>
              </div>
              <div className="fw-semibold small">
                {pickI18n(popupBulletin.title, bulletinDisplayLang(popupBulletin.id))}
              </div>
              {renderBulletinBody(popupBulletin, bulletinDisplayLang(popupBulletin.id))}
              <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                {popupBulletin.created_at}
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal show={Boolean(previewImageUrl)} onHide={handleCloseImagePreview} centered size="xl">
        <Modal.Body
          className="p-0 d-flex align-items-center justify-content-center"
          style={{ background: '#111', minHeight: 320 }}
        >
          {previewImageUrl && (
            <img
              src={previewImageUrl}
              alt=""
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                objectFit: 'contain',
              }}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
